import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOption from "components/Common/DropdownOption";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { AI_MODEL_NEWS_STOCK_OPTIONS, SYSTEM_CONFIG_KEY } from "helpers/constans";
import React, { useEffect, useState } from "react";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { getConfiguration, getTemplateSampleNewsStockExcel, importGenerateNewsStockByExcel } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    isOpen: boolean;
    onRefresh?: () => void,
    onClose?: () => void,
}

registerLocale("en", en);
registerLocale("ko", ko);

const FILE_SIZE = 10 * 1024 * 1024; // 10MB
const SUPPORTED_FORMATS = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ModalImportGenerateNewStock = ({
    isOpen,
    onRefresh,
    onClose,
}: Props) => {
    const { t, i18n } = useTranslation();

    const AI_MODEL_NEWS_STOCK_OPTIONS_LANG: any = AI_MODEL_NEWS_STOCK_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isOpenBlockedSites, setIsOpenBlockedSites] = useState<boolean>(false);
    const [blockedSites, setBlockedSites] = useState<string[]>([]);


    const [isLoadingDownloadFile, setIsLoadingDownloadFile] = useState<boolean>(false);

    const [isOpenArticleBlockedSites, setIsOpenArticleBlockedSites] = useState<boolean>(false);
    const [articleBlockedSites, setArticleBlockedSites] = useState<{ blocked_sites: string[], total: number } | null>({ blocked_sites: [], total: 0 });

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const data = {
                files: values?.file || null,
                ai_model: values?.ai_model?.value || AI_MODEL_NEWS_STOCK_OPTIONS_LANG[0]?.value,
                schedule_upload_date: (!!values?.is_schedule_upload_date && !!values?.schedule_upload_date) ? values?.schedule_upload_date : null,
            };
            const response: any = await importGenerateNewsStockByExcel(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                const article_blocked_sites = (response?.data || [])?.filter((item: any) => item?.errors?.length > 0);
                const total = (response?.data || [])?.length;
                if (article_blocked_sites?.length > 0) {
                    setArticleBlockedSites(() => ({
                        blocked_sites: article_blocked_sites,
                        total: total,
                    }));
                    handleShowArticleBlockedSites();

                } else {
                    toast(
                        `${t("The process has been completed.")}`,
                        CONFIG_OPTION_TOAST_NORMAL
                    );
                }
                onRefresh && onRefresh();

            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        file: Yup.mixed()
            .test(
                'fileSize',
                t('File too large. Max size is 10MB.'),
                (value: any) => !value || (value?.size <= FILE_SIZE)
            )
            .test(
                'fileFormat',
                t('Unsupported Format. Please upload an Excel file.'),
                (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
            ).required(`${t("File is required")}`),
        ai_model: Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
        }).nullable().required(`${t("AI Model is required")}`),
        is_schedule_upload_date: Yup.boolean().nullable(),
        schedule_upload_date: Yup.string().nullable(),
    });

    const formik = useFormik({
        initialValues: {
            file: null,
            ai_model: AI_MODEL_NEWS_STOCK_OPTIONS_LANG[0],
            is_schedule_upload_date: false,
            schedule_upload_date: null,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });


    const handleChangeFile = (value: any | null) => {
        formik?.setFieldValue('file', value?.target?.files[0] || null);
    }

    const handleDownloadExcel = async (e: any) => {
        try {
            e.preventDefault();
            if (isLoadingDownloadFile) {
                return;
            }
            setIsLoadingDownloadFile((_prev) => true);
            const response: any = await getTemplateSampleNewsStockExcel({});
            if (response?.data) {
                setIsLoadingDownloadFile((_prev) => false);
                const link = document.createElement('a');
                link.href = response?.data?.import_file_url;
                link.download = 'Download Sample Excel';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setIsLoadingDownloadFile((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoadingDownloadFile((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }

    const handleClose = () => {
        formik.resetForm();
        formik.setErrors({});
        onClose && onClose();
    }

    const handleShowBlockedSites = () => {
        setIsOpenBlockedSites((_prev) => true);
    }

    const handleCloseBlockedSites = () => {
        setIsOpenBlockedSites((_prev) => false);
    }

    const handleShowArticleBlockedSites = () => {
        setIsOpenArticleBlockedSites((_prev) => true);
    }

    const handleCloseArticleBlockedSites = () => {
        setIsOpenArticleBlockedSites((_prev) => false);
    }

    const handleGetConfig = async () => {
        try {
            const response: any = await getConfiguration();
            if (response?.code === 200) {
                const _configs = response?.data?.list || [];
                const _ai_model = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.STOCK_NEWS_GENERATE_AI_MODEL) || AI_MODEL_NEWS_STOCK_OPTIONS_LANG[0];
                formik?.setFieldValue('ai_model', _ai_model);
                const _blocked_sites = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.STOCK_NEWS_BLOCKED_SITES)?.value || '';
                const blocked_sites = _blocked_sites?.split('\n') || [];
                setBlockedSites(() => blocked_sites);
            }
        } catch (error) {
            return error;
        }
    };

    useEffect(() => {
        if (isOpen) {
            handleGetConfig();
        }
    }, [isOpen]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="md"
                scrollable={false}
            >
                <ModalHeader toggle={handleClose}>
                    <>{t("Button Import Bulk Generate Boosting News V2")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <div>
                        <Container>
                            <Row>
                                <form onSubmit={formik.handleSubmit}>
                                    <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                                        <Row>
                                            <Col sm={12} md={12} xl={12} className='mt-3 '>
                                                <Label htmlFor="formSizeDefault" className="form-label">{t('AI Model')}<span className="text-danger"> *</span></Label>
                                                <DropdownOption
                                                    name="ai_model"
                                                    dataList={AI_MODEL_NEWS_STOCK_OPTIONS_LANG || []}
                                                    placeholder={`${t("AI Model")}...`}
                                                    className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                                    classNamePrefix="name-prefix"
                                                    initialValue={formik?.values?.ai_model}
                                                    onChangeSelect={(e: any) => {
                                                        formik?.setFieldValue('ai_model', e)
                                                    }}
                                                />
                                            </Col>
                                            <Col sm={12} md={12} xl={12} className='mt-3 '>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <Label htmlFor="formSizeDefault" className="form-label">{t('Bulk Articles') + ' (' + t('Excel File') + ')'} <span className="text-danger"> *</span></Label>

                                                    <span className="text-primary cursor-pointer d-flex" onClick={() => handleShowBlockedSites()}>
                                                        <div style={{ transform: 'translateY(1px)' }}><i className="ri-information-line me-1" ></i></div>{t('Blocked Sites')}
                                                    </span>
                                                </div>
                                                <div className="custom-file-button">
                                                    <Input className="form-control" accept=".xls,.xlsx"
                                                        label="Upload Your File"
                                                        data-title="Upload Your File"
                                                        id="formSizeDefault"
                                                        type="file"
                                                        onChange={handleChangeFile}
                                                    />
                                                    {!formik?.values?.file && <div className="text-upload">{t('No file chosen')}</div>}
                                                    <span className="icon-upload"> <i className="ri-upload-line" /></span>
                                                </div>
                                                {formik.touched.file && formik.errors.file ? (
                                                    <div className="text-danger mt-2">{formik.errors.file}</div>
                                                ) : null}
                                            </Col>
                                            <Col sm={12} md={12} xl={12} className='mt-3'>
                                                <div className="form-check form-switch mb-0">
                                                    <Input className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="is_schedule_upload_date"
                                                        name="is_schedule_upload_date"
                                                        checked={!!formik?.values?.is_schedule_upload_date}
                                                        onChange={(event: any) => {
                                                            formik.setFieldValue("is_schedule_upload_date", Number(event?.target.checked))
                                                        }}
                                                    />
                                                    <Label className={`form-check-label ${!!formik?.values?.is_schedule_upload_date ? 'text-primary' : 'text-muted'}`} for="is_schedule_upload_date">{t('Schedule Upload Date')}</Label>
                                                </div>
                                                {!!formik?.values?.is_schedule_upload_date && <div
                                                    className="mt-2"
                                                >
                                                    <div className="date-picker-wrapper-custom ">
                                                        <DatePicker
                                                            className="form-control search"
                                                            placeholderText={`${t('Schedule Date')}...`}
                                                            isClearable={true}
                                                            showTimeSelect
                                                            dateFormat="yyyy-MM-dd HH:mm"
                                                            autoComplete="off"
                                                            timeCaption={t("Time")}
                                                            timeIntervals={1}
                                                            timeFormat="HH:mm"
                                                            minDate={new Date()}
                                                            locale={i18n?.language === 'ko' ? 'ko' : 'en'}
                                                            selected={formik?.values?.schedule_upload_date ? new Date(formik?.values?.schedule_upload_date) : null}
                                                            name={`schedule_upload_date`}
                                                            onChange={(value: Date | null) => {
                                                                formik.setFieldValue(`schedule_upload_date`, value ? moment(value).format("YYYY-MM-DD HH:mm") : "");
                                                            }}
                                                        />
                                                    </div>
                                                    {formik.touched.schedule_upload_date && formik.errors.schedule_upload_date ? (
                                                        <div className="text-danger mt-2">{formik.errors.schedule_upload_date || ''}</div>
                                                    ) : null}
                                                </div>}
                                            </Col>
                                            <Col sm={6} md={6} xl={6} className='mt-0 mt-sm-4 text-start ' style={{ paddingTop: '26px' }}>
                                                <a href={'#'} onClick={handleDownloadExcel} download="기사_등록_샘플_파일.xlsx" className="stretched-link hover-underline">
                                                    {isLoadingDownloadFile && <Spinner size="sm me-1" ></Spinner>} {t('Download Sample Excel')}
                                                </a>
                                            </Col>
                                            <Col sm={6} md={6} xl={6} className='mt-4 pt-1 text-end'>
                                                <button
                                                    style={{ width: '160px' }}
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="btn btn-primary fs-14 rounded-pill">
                                                    {isLoading ? <Spinner size="sm me-1" ></Spinner> : <></>}
                                                    {t('Button Import Bulk')}
                                                </button>
                                            </Col>
                                        </Row>
                                    </div>
                                </form>
                            </Row >
                        </Container >
                    </div>
                </ModalBody>
            </Modal>
            <Modal
                isOpen={isOpenBlockedSites}
                centered={true}
                size="md"
                scrollable={false}
            >
                <ModalHeader toggle={handleCloseBlockedSites}>{t('Blocked Sites')}</ModalHeader>
                <ModalBody>
                    <div>
                        <div className="d-flex flex-column gap-2">
                            {blockedSites?.map((item: any) => (
                                <div key={item} className="text-start">{item}</div>
                            ))}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="button"
                        className="btn btn-secondary fs-14 rounded-pill"
                        style={{ width: '100px' }}
                        onClick={handleCloseBlockedSites}
                    >
                        {t('Button Close')}
                    </button>
                </ModalFooter>
            </Modal>
            <Modal
                isOpen={isOpenArticleBlockedSites}
                centered={true}
                size="lg"
                scrollable={false}
            >
                <ModalHeader toggle={handleCloseArticleBlockedSites} >{t('Notification')}</ModalHeader>
                <ModalBody>
                    <div className="d-flex flex-column gap-2 font-weight-bold mb-2 fs-16 text-muted">{t('Article Errors')}: {articleBlockedSites?.blocked_sites?.length} / {articleBlockedSites?.total}</div>
                    <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>{t('Keyword')}</th>
                                    <th style={{ maxWidth: '300px' }}>{t('URL')}</th>
                                    <th style={{ maxWidth: '200px' }}>{t('Error')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articleBlockedSites?.blocked_sites?.map((item: any) => (
                                    <tr key={item?.keyword}>
                                        <td>{item?.keyword || '-'}</td>
                                        <td><Link to={item?.url || ''} target="_blank" className="text-primary">{item?.url || '-'}</Link></td>
                                        <td className="text-start text-danger">{item?.errors?.join(', ') || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="button"
                        className="btn btn-secondary fs-14 rounded-pill"
                        style={{ width: '100px' }}
                        onClick={handleCloseArticleBlockedSites}
                    >
                        {t('Button Close')}
                    </button>
                </ModalFooter>
            </Modal>
        </React.Fragment >
    );
};

export default ModalImportGenerateNewStock;
